import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Grid,
  InputLabel,
  TextField,
  Typography,
  debounce,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import parse from "autosuggest-highlight/parse";
import axios from "axios";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_KEY;

const autocompleteService = { current: null };

function getAddressLabel(option) {
  if (typeof option === "string") return option;
  return option?.description || option?.formatted_address || "";
}

function optionFromAddress(address) {
  const label = String(address ?? "").trim();
  if (!label) return null;
  return {
    description: label,
    structured_formatting: {
      main_text: label,
      secondary_text: "",
      main_text_matched_substrings: [],
    },
  };
}

function loadGoogleMapsScript() {
  if (typeof window === "undefined") return;
  if (document.querySelector("#google-maps")) return;

  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", "google-maps");
  script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
  document.querySelector("head")?.appendChild(script);
}

/**
 * Google Places autocomplete styled like MaInput.
 *
 * @param {string} label
 * @param {string} value - controlled address string
 * @param {(payload: { address: string, latitude?: number, longitude?: number }) => void} onAddressChange
 * @param {boolean} [disabled]
 * @param {string} [placeholder]
 * @param {string} [borderColor]
 */
export default function GoogleAddressAutocomplete({
  label = "Address",
  value = "",
  onAddressChange,
  disabled = false,
  placeholder = "Find a location",
  borderColor = "#E5E7EB",
}) {
  const [selected, setSelected] = useState(null);
  const [inputValue, setInputValue] = useState(value || "");
  const [options, setOptions] = useState([]);
  const scriptLoaded = useRef(false);

  useEffect(() => {
    const nextAddress = value || "";
    setInputValue(nextAddress);
    setSelected(optionFromAddress(nextAddress));
  }, [value]);

  if (typeof window !== "undefined" && !scriptLoaded.current) {
    loadGoogleMapsScript();
    scriptLoaded.current = true;
  }

  const fetchPredictions = useMemo(
    () =>
      debounce((request, callback) => {
        autocompleteService.current?.getPlacePredictions(request, callback);
      }, 400),
    [],
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window.google?.maps?.places) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(selected ? [selected] : []);
      return undefined;
    }

    fetchPredictions({ input: inputValue }, (results) => {
      if (!active) return;

      let newOptions = [];
      if (selected) newOptions = [selected];
      if (results) newOptions = [...newOptions, ...results];
      setOptions(newOptions);
    });

    return () => {
      active = false;
    };
  }, [selected, inputValue, fetchPredictions]);

  const geocodeByPlaceId = async (placeId) => {
    if (!GOOGLE_MAPS_API_KEY || !placeId) return;

    try {
      const { data } = await axios.get(
        "https://maps.googleapis.com/maps/api/geocode/json",
        {
          params: { place_id: placeId, key: GOOGLE_MAPS_API_KEY },
        },
      );

      const result = data?.results?.[0];
      if (!result) return;

      onAddressChange?.({
        address: result.formatted_address,
        longitude: result.geometry?.location?.lng,
        latitude: result.geometry?.location?.lat,
      });
    } catch {
      // Parent validation handles empty address on save.
    }
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      fontWeight: 500,
      color: "#000",
      fontSize: "13px",
      minHeight: "46px",
      "& fieldset": {
        borderColor,
        borderRadius: "10px",
        borderWidth: "1px",
      },
      "&:hover fieldset": { borderColor },
      "&.Mui-focused fieldset": { borderColor },
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#717171",
      opacity: 1,
    },
  };

  return (
    <div>
      {label ? (
        <InputLabel
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            mb: 1,
            fontFamily: "lato",
            color: "#272727",
          }}
        >
          {label}
        </InputLabel>
      ) : null}

      <Autocomplete
        id="google-address-autocomplete"
        disabled={disabled}
        freeSolo
        getOptionLabel={getAddressLabel}
        filterOptions={(x) => x}
        options={options}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={selected}
        inputValue={inputValue}
        isOptionEqualToValue={(option, currentValue) =>
          getAddressLabel(option) === getAddressLabel(currentValue)
        }
        noOptionsText="No locations"
        onChange={(_, newValue) => {
          const nextSelected =
            typeof newValue === "string" ? optionFromAddress(newValue) : newValue;
          setSelected(nextSelected);
          if (newValue?.place_id) {
            geocodeByPlaceId(newValue.place_id);
          } else if (typeof newValue === "string") {
            onAddressChange?.({ address: newValue });
          } else if (!newValue) {
            onAddressChange?.({ address: "" });
          }
        }}
        onInputChange={(_, newInputValue, reason) => {
          setInputValue(newInputValue);
          if (reason === "input") {
            onAddressChange?.({ address: newInputValue });
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            fullWidth
            sx={textFieldSx}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          const label = getAddressLabel(option);
          const matches =
            option.structured_formatting?.main_text_matched_substrings || [];
          const parts = parse(
            option.structured_formatting?.main_text || label,
            matches.map((match) => [
              match.offset,
              match.offset + match.length,
            ]),
          );

          return (
            <li key={key} {...optionProps}>
              <Grid container alignItems="center" wrap="nowrap">
                <Grid item sx={{ display: "flex", width: 44, flexShrink: 0 }}>
                  <LocationOnIcon
                    sx={{ color: "text.secondary", fontSize: 18 }}
                  />
                </Grid>
                <Grid
                  item
                  sx={{
                    minWidth: 0,
                    flex: 1,
                    wordWrap: "break-word",
                  }}
                >
                  {parts.map((part, index) => (
                    <Box
                      key={index}
                      component="span"
                      sx={{
                        fontWeight: part.highlight ? 700 : 400,
                        fontSize: "13px",
                      }}
                    >
                      {part.text}
                    </Box>
                  ))}
                  <Typography variant="body2" color="text.secondary">
                    {option.structured_formatting?.secondary_text}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          );
        }}
      />
    </div>
  );
}
